/* eslint-disable no-nested-ternary */
import { FiExternalLink } from 'react-icons/fi';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import { MdOutlineKeyboardArrowDown } from 'react-icons/md';
import {
  RootState, useGetAllBusinessesQuery, useGetAllUserEventsQuery, useGetBusinessByIdQuery, useGetUserBusinessesQuery,
} from '../store';
import { setSelectedEvent } from '../store/slices/events';
import Button from '../components/atoms/Button';
import useNavigateWithQuery from '../hooks/useNavigateWithQuery';
import useGetUserRole from '../hooks/useGetUserRole';
import EventDTO, { EventPublicStatus } from '../models/event';
import Input from '../components/atoms/Input';
import Select from '../components/atoms/Select/Select';
import AdminEventsSkeleton from '../components/AdminEventsSkeleton';
import AdminSectionTitle from '../components/AdminSectionTitle';
import authenticate from '../utils/authenticate';
import EventMenuOption from '../components/EventMenuOption';
import { UserRole, UserType } from '../models/user';
import LandingEventCard from '../components/LandingEventCard';
import getToastText from '../utils/getToastText';
import getToastColor from '../utils/getToastColor';
import CustomModal from '../components/atoms/CustomModal';
import InvitationNotification from '../components/InvitationNotification';
import {
  IconCard1, IconMove1, IconMove2, IconPlay2, IconPlay3, IconSmile2,
} from '../components/icons';
import isAllowed from '../utils/isAllowed';

interface Options {
  title: string;
  info: string;
  buttonLabel: string;
  redirectPath: string;
  allowedUsers: UserRole[];
  hidden?: boolean;
  icon: React.ReactNode;
}

interface AdminEventsProps {
  allUserEvents?: boolean;
}

const AdminEvents: React.FC<AdminEventsProps> = ({ allUserEvents = false }) => {
  const navigate = useNavigateWithQuery();
  const dispatch = useDispatch();
  const role = useGetUserRole({ skip: allUserEvents });
  const userData = useSelector((state: RootState) => state.auth.user);
  const { business_id: businessId } = useParams();
  const { data: events, refetch: refetchEvents, isLoading: allEventsLoading } = useGetAllUserEventsQuery({ userId: userData.id, businessId: allUserEvents ? undefined : businessId });
  const { data: allBusinesses } = useGetAllBusinessesQuery(
    { query: { active: true } },
    {
      skip: !userData || userData.type !== 'ROOT',
    },
  );
  const { data: userBusinesses } = useGetUserBusinessesQuery({ userId: userData.id }, { skip: !userData || !userData.id || userData.type === 'ROOT' });
  const { data: business, isLoading, refetch } = useGetBusinessByIdQuery(businessId || '', { skip: allUserEvents });
  const [authorizedEvents, setAuthorizedEvents] = useState<EventDTO[]>([]);
  const [filterParams, setFilterParams] = useState({
    status: '',
    dateFrom: '',
    dateTo: '',
    business: '',
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [visibleEventsCount, setVisibleEventsCount] = useState(6);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<string>('');

  useEffect(() => {
    if (!allUserEvents) {
      refetch();
    }
    refetchEvents();
  }, [businessId, refetch, refetchEvents, allUserEvents]);

  useEffect(() => {
    const baseEvents = events
      ?.filter((e) => {
        const searchFilter = e.name.toLowerCase().includes(searchTerm.toLowerCase());
        const statusFilter = !filterParams.status || e.public_status === filterParams.status;
        const dateFilter = (!filterParams.dateFrom || new Date(e.date) >= new Date(filterParams.dateFrom))
          && (!filterParams.dateTo || new Date(e.date) <= new Date(filterParams.dateTo));
        const businessFilter = !filterParams.business || e.business_id === filterParams.business;

        return searchFilter && statusFilter && dateFilter && businessFilter;
      })
      ?.sort((a, b) => {
        if (sortBy === 'from-a-to-z') {
          return a.name.localeCompare(b.name);
        }
        if (sortBy === 'from-z-to-a') {
          return b.name.localeCompare(a.name);
        }
        if (sortBy === 'newest') {
          return new Date(b.date).getTime() - new Date(a.date).getTime();
        }
        if (sortBy === 'oldest') {
          return new Date(a.date).getTime() - new Date(b.date).getTime();
        }
        // Default sort by date (newest first)
        return new Date(b.date).getTime() - new Date(a.date).getTime();
      });

    setAuthorizedEvents(baseEvents || []);
  }, [role, business, events, allUserEvents, filterParams, searchTerm, sortBy]);

  useEffect(() => {
    setVisibleEventsCount(window.innerWidth < 768 ? 3 : 6);
  }, []);

  const handleEventSelect = (eventId: string) => {
    let event: EventDTO | undefined;
    if (allUserEvents) {
      event = events?.find((e) => e.id === eventId);
    } else {
      event = business?.events?.find((e) => e.id === eventId);
    }
    if (event) {
      dispatch(setSelectedEvent(event));
    }
    if (!allUserEvents) {
      navigate(`${eventId}`);
    } else {
      navigate(`/admin/${event?.business_id}/events/${eventId}`);
    }
  };

  const handleShowMore = () => {
    setVisibleEventsCount((prev) => prev + (window.innerWidth < 768 ? 3 : 6));
  };

  const options: Options[] = [
    {
      title: 'Información de la productora',
      info: 'En este apartado podrás editar la información y configuración de tu productora',
      buttonLabel: 'Editar productora',
      redirectPath: 'edit-business',
      allowedUsers: [UserRole.EDITOR, UserRole.ADMIN, UserRole.ROOT],
      icon: <IconPlay3 className='w-10 h-10 ml-2 mt-1' />,
    },
    {
      title: 'CRM - Gestión de clientes',
      info: 'En este apartado podrás ver todos los clientes de tu productora',
      buttonLabel: 'Ver clientes',
      redirectPath: 'customers',
      allowedUsers: [UserRole.ADMIN, UserRole.ROOT],
      icon: <IconMove1 className='w-10 h-10 ml-2 mt-1' />,
    },
    {
      title: 'Gestión de usuarios',
      info: 'En este apartado podrás administrar los permisos de los usuarios de tu productora',
      buttonLabel: 'Gestionar usuarios',
      redirectPath: 'users',
      allowedUsers: [UserRole.EDITOR, UserRole.ADMIN, UserRole.ROOT],
      icon: <IconPlay2 className='w-10 h-10 ml-2 mt-1' />,
    },
    {
      title: 'Configurar cobro',
      info: 'En este apartado podrás configurar el cobro de entradas',
      buttonLabel: 'Configurar cobro',
      redirectPath: 'mercadopago',
      allowedUsers: [UserRole.ADMIN, UserRole.ROOT],
      icon: <IconCard1 className='w-10 h-10 ml-2 mt-1' />,
      hidden: !business?.has_mp_split,
    },
    {
      title: 'Cajas',
      info: 'En este apartado podrás gestionar las cajas de tu productora para la venta presencial',
      buttonLabel: 'Gestionar cajas',
      redirectPath: 'cash-register',
      allowedUsers: [UserRole.ADMIN, UserRole.ROOT],
      icon: <IconMove2 className='w-10 h-10 ml-2 mt-1' />,
      hidden: !business?.has_cash_registers,
    },
    {
      title: 'Socios',
      info: 'En este apartado podrás administrar los socios de tu productora',
      buttonLabel: 'Gestionar socios',
      redirectPath: 'members',
      allowedUsers: [UserRole.ADMIN, UserRole.ROOT],
      icon: <IconSmile2 className='w-10 h-10 ml-2 mt-1' />,
      hidden: !business?.has_members,
    },
  ];

  if (isLoading || !role || allEventsLoading) {
    return <AdminEventsSkeleton />;
  }

  return (
    <div className="flex my-6 flex-col align-start gap-8 overflow-visible">
      {!allUserEvents ? <AdminSectionTitle title={`${business?.name}`} mainButtons={[
        ...(isAllowed(userData, [UserRole.EDITOR, UserRole.ADMIN, UserRole.ROOT], businessId ?? '') ? [{
          label: 'Crear nuevo evento',
          onClick: () => navigate('create'),
          variant: 'primary' as const,
          image: '/edit-3.svg',
          imageInverted: true,
          mobileLabel: 'Crear nuevo evento',
        }] : []),
      ]}
        icon={<IconPlay2 className='w-12 h-12 rotate-180' />} />
        : <AdminSectionTitle title='Eventos' />}
      {allUserEvents && <InvitationNotification />}
      <div className="flex flex-col w-full md:flex-row gap-4 items-center">
        <div className="w-full bg-surface-base rounded-3xl h-12">
          <Input
            id="search-event"
            label="Buscar evento"
            placeholder="Buscar evento"
            onChange={(e) => setSearchTerm(e.target.value)}
            value={searchTerm}
            isSearch
          />
        </div>
        <Select
          placeholder="Ordenar eventos"
          id="sort-events"
          label=""
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          options={[
            { value: 'newest', label: 'Del más nuevo al más antiguo' },
            { value: 'oldest', label: 'Del más antiguo al más nuevo' },
            { value: 'from-a-to-z', label: 'Nombre de la A a la Z' },
            { value: 'from-z-to-a', label: 'Nombre de la Z a la A' },
          ]}
        />
        <Button variant="secondary" size="sm" onClick={() => setIsModalOpen(true)}>
          Filtrar eventos
        </Button>
      </div>

      {!isLoading && authorizedEvents.length > 0 && (
        <div className='flex flex-col gap-8 glass-surface'>
          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
            {authorizedEvents.slice(0, visibleEventsCount).map((event) => (
              <div key={event.id} className='w-full'>
                <LandingEventCard
                  event={event}
                  onSelect={handleEventSelect}
                  textToast={getToastText(event.public_status)}
                  colorToast={getToastColor(event.public_status)}
                  textButton='Ver evento'
                />
              </div>
            ))}
          </div>
          {visibleEventsCount < authorizedEvents.length && (
            <div className="flex items-center gap-4 text-xl justify-center mb-2 lg:mb-0">
              <div
                onClick={handleShowMore}
                className="flex items-center gap-2 text-primary-500 cursor-pointer whitespace-nowrap justify-center"
              >
                <MdOutlineKeyboardArrowDown className='text-primary-500 w-8 h-8' />
                <p className='w-full text-center'>Mostrar más eventos</p>
                <MdOutlineKeyboardArrowDown className='text-primary-500 w-8 h-8' />
              </div>
            </div>
          )}
        </div>
      )}

      {!isLoading && authorizedEvents.length === 0 && !allUserEvents && (
        <div className="flex flex-col items-center">
          <div className="text-lg text-center w-full">
            {events?.length === 0 ? (
              isAllowed(userData, [UserRole.EDITOR, UserRole.ADMIN, UserRole.ROOT], businessId ?? '') ? (
                <div className='flex flex-col items-center gap-2 glass-surface'>
                  <h1 className="text-2xl font-medium">¡Empecemos creando tu primer evento!</h1>
                  <p className='mb-2'>Actualmente tu negocio no tiene eventos asociados</p>
                  <div className='mx-auto w-fit'>
                    <Button
                      type="submit"
                      variant="primary"
                      className="mb-2"
                      onClick={() => navigate('create')}
                    >
                      Crear nuevo evento
                    </Button>
                  </div>
                </div>) : (
                <div className='flex flex-col items-center gap-2 glass-surface'>
                  <h1 className="text-2xl font-medium">Esta productora no tiene eventos asociados</h1>
                  <p>
                    Comunicate con el productor si crees que es un error.
                  </p>
                </div>
              )
            ) : (
              <div className='flex flex-col items-center gap-2 glass-surface'>
                <h1 className="text-2xl font-medium">¡No encontramos el evento que buscas!</h1>
                <p>
                  Probá con una combinación diferente de filtros.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {authorizedEvents.length === 0 && allUserEvents && events?.length === 0 && (
        <div className="flex flex-col items-center justify-center w-full glass-surface">
          <div className="text-lg text-center">
            <h1 className="text-2xl font-medium">No tenés eventos asociados</h1>
            <p className='mb-2'>Actualmente tu cuenta no tiene eventos asociados</p>
            {(
              userData.type === UserType.ROOT
              || userData.user_businesses.some((ub) => ub.active && ub.permissions === UserRole.ADMIN)
              || (userBusinesses && userBusinesses.length === 0)
            ) && (
              <Button variant="primary" onClick={() => navigate('/admin/businesses')}>
                <div className='flex items-center gap-2'>
                  Crear
                  <FiExternalLink className='w-4 h-4' />
                </div>
              </Button>
            )}
          </div>
        </div>
      )}

      {(isAllowed(userData, [UserRole.EDITOR, UserRole.ADMIN, UserRole.ROOT], businessId ?? '')) && !allUserEvents && (
        <div className='flex flex-col gap-4 glass-surface'>
          <h1 className='text-2xl font-medium'>Ajustes de la productora </h1>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {options.map(
              (option) => (!option.hidden && authenticate(option.allowedUsers, role)) && (
                <div key={option.title}>
                  <EventMenuOption
                    id={''}
                    title={option.title}
                    info={option.info}
                    buttonLabel={option.buttonLabel}
                    redirectPath={option.redirectPath}
                    icon={option.icon}
                    businessId={businessId}
                  />
                </div>
              ),
            )}
          </div>
        </div>
      )}
      <CustomModal
        isOpen={isModalOpen}
        onRequestClose={() => setIsModalOpen(false)}
        hideCancelButton
        overflowVisible
      >
        <div className="overflow-visible">
          <h1>Filtrar eventos</h1>
          <p className='text-sm text-text-tertiary'>Seleccioná entre las siguientes opciones los filtros necesarios para encontrár el evento que buscás.</p>
          <div className='flex flex-col gap-2 my-2'>
            <div className='flex gap-2 overflow-y-visible'>
              <Select
                id="show-inactive-events"
                label="Estado"
                value={filterParams.status}
                onChange={(e) => setFilterParams({
                  ...filterParams,
                  status: e.target.value as EventPublicStatus,
                })}
                options={[
                  { value: 'ready', label: 'Aprobado' },
                  { value: 'pending_approval', label: 'Pendiente de aprobación' },
                  { value: 'no_tickets_available', label: 'No hay tickets disponibles' },
                  { value: 'expired', label: 'Finalizado' },
                  { value: 'inactive', label: 'Inactivo' },
                ]}
              />
            </div>
            <div className="flex flex-col md:flex-row gap-2">
              <Input
                id="date-from-filter"
                label="Fecha desde"
                labelAbove
                type="date"
                onChange={(e) => setFilterParams({
                  ...filterParams,
                  dateFrom: e.target.value,
                })}
                value={filterParams.dateFrom}
              />
              <Input
                id="date-to-filter"
                label="Fecha hasta"
                labelAbove
                type="date"
                onChange={(e) => setFilterParams({
                  ...filterParams,
                  dateTo: e.target.value,
                })}
                value={filterParams.dateTo}
              />
            </div>
            {allUserEvents && (
              <Select
                id="business-filter"
                label="Productora"
                value={filterParams.business}
                onChange={(e) => setFilterParams({
                  ...filterParams,
                  business: e.target.value,
                })}
                options={(userBusinesses?.map((b) => ({ value: b.id, label: b.name })) || allBusinesses?.map((b) => ({ value: b.id, label: b.name })) || [])}
              />
            )}
          </div>
          <div className='flex gap-2 mt-4'>
            <Button variant="secondary" onClick={() => {
              setFilterParams({
                status: '',
                dateFrom: '',
                dateTo: '',
                business: '',
              });
              setIsModalOpen(false);
            }}>
              Sacar filtros
            </Button>
            <Button variant="primary" onClick={() => setIsModalOpen(false)}>
              Aplicar
            </Button>
          </div>
        </div>
      </CustomModal>
    </div>
  );
};
export default AdminEvents;
